import { router, Link, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SafeAreaView, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import SecurityModal from "@/components/modal/utils/SecurityModal";
import SessionExpiredModal from "@/components/modal/utils/SessionExpiredModal";
import { clearAllValues, setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { getUserStorage, storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { setBridgeStatusInCookies } from "@/utils/cookies/Cookies";

type FormData = {
  email: string;
  password: string;
  code?: string;
};

interface CustomerType {
  redirectUrl: string;
  accessToken: string;
  expiresIn: number;
  customerId: string;
  name: string | null;
}

interface LoginResponseType {
  userId: string;
  customers: CustomerType[];
  lang: string;
}

export async function loginHandler(data: FormData): Promise<LoginResponseType> {
  try {
    const currentUserId = storage.getString("user.auth.userId");
    if (currentUserId) {
      const userStorage = getUserStorage(currentUserId);
      userStorage.clearAll();
    }
    storage.clearAll();
    const response = await sendRequest(
      "post",
      "/login",
      {
        email: data.email,
        password: data.password,
      },
      false,
      false,
      data.code
    );

    if (response.otp?.isTwoFactorAuthenticationEnabled === true) {
      storage.set("user.auth.isOtpEnabled", true);
    } else {
      storage.set("user.auth.isOtpEnabled", false);
    }

    const expireIn =
      (response.expiresIn || response.customers[0]?.expiresIn) + Date.now();

    if (response.redirectUrl !== undefined) {
      storage.set("user.auth.token", response.accessToken);
      storage.set("user.auth.userId", response.userId);
      storage.set("user.auth.expireIn", expireIn);
      const userStorage = getUserStorage(response.userId);
      userStorage.set("user.authentication", true);
      router.replace(`${response.redirectUrl}`);
    } else if (
      response.customers !== undefined &&
      response.customers[0] &&
      response.customers[0].redirectUrl !== undefined
    ) {
      storage.set("user.auth.token", response.customers[0].accessToken);
      storage.set("user.auth.userId", response.userId);
      storage.set("user.auth.expireIn", expireIn);
      const userStorage = getUserStorage(response.userId);
      userStorage.set("user.authentication", true);
      if (
        response.customers[0].redirectUrl === "/corporation/documents" ||
        response.customers[0].redirectUrl === "/individual/documents"
      ) {
        router.replace("/documents");
      } else {
        router.replace(`${response.customers[0].redirectUrl}`);
      }
    } else {
      storage.set("user.auth.token", response.customers[0].accessToken);
      storage.set("user.auth.userId", response.userId);
      storage.set("user.auth.expireIn", expireIn);
      const userStorage = getUserStorage(response.userId);
      userStorage.set("user.authentication", true);

      const bridgeStatus = response.customers[0]?.bridgeCustomerId
        ? "ACTIVE"
        : "PENDING";
      setBridgeStatusInCookies(bridgeStatus);
      router.replace("/home");
    }

    return response;
  } catch (error: any) {
    throw error;
  }
}

export default function Login() {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showExpireModal, setShowExpireModal] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const states = useAppSelector((state: any) => state);

  const { control, handleSubmit, formState, reset, getValues } =
    useForm<FormData>({
      mode: "onSubmit",
    });

  const { isSubmitting } = formState;

  const logoutUser = () => {
    const currentUserId = storage.getString("user.auth.userId");
    const userStorage = getUserStorage(currentUserId);
    storage.clearAll();
    userStorage.clearAll();
    dispatch(clearAllValues());
  };

  useFocusEffect(
    React.useCallback(() => {
      if (states.sessionExpiredAlert) {
        setShowExpireModal(true);
      }
      reset();
      logoutUser();
      return () => {};
    }, [states.sessionExpiredAlert])
  );

  useEffect(() => {
    const checkLoginError = async () => {
      reset();
      if (states.loginError === true) {
        Toast.show({
          type: "error",
          autoHide: false,
          props: {
            text1: t("common:toast.error"),
            text2: t("common:toast.subtitle.loginerror"),
          },
        });
      }
    };
    checkLoginError();
  }, []);

  const onSubmit = async (data: FormData) => {
    dispatch(clearAllValues());
    try {
      const response = await loginHandler(data);
      if (response) {
        dispatch(setForm({ email: data.email, password: data.password }));
      }
    } catch (error: any) {
      if (error.data.message === "OTP_REQUIRED") {
        setShowModal(true);
        return;
      }
      setShowModal(false);
    }
  };

  return (
    <SafeAreaView className="flex items-center justify-center w-full h-full bg-white1">
      <View className="flex-grow items-center justify-center w-2/3 h-fit">
        <Logo className="m-1" />
        <View className="m-2">
          <Title text={t("login:welcomeback")} />
        </View>
        <Link href="/signup" asChild>
          <Text className="text-black text-sm text-blue5">
            {t("login:noaccount")}
          </Text>
        </Link>
        <Input
          label={t("login:email")}
          control={control}
          name="email"
          handleChange={(text: string) => setEmail(text)}
          placeholder={t("common:terms.typeemail")}
        />
        <Input
          label={t("login:password")}
          control={control}
          name="password"
          handleChange={(text: string) => setPassword(text)}
          placeholder={t("common:terms.typepassword")}
          isPassword
        />
        <Button
          label={t("common:button.signin")}
          onClick={handleSubmit(onSubmit)}
        />
        <Link href="/passwordrecovery" asChild>
          <Text className="text-black text-sm text-blue5">
            {t("login:forgotpassword")}
          </Text>
        </Link>
      </View>
      <View className="h-fit">
        <Copyright />
      </View>
      {isSubmitting && <Loader />}
      <SecurityModal
        visible={showModal}
        onClose={() => {
          setShowModal(false);
          reset();
        }}
        email={email}
        password={password}
        sendCode={(code: string) => {
          onSubmit({
            email: getValues("email"),
            password: getValues("password"),
            code,
          });
        }}
      />
      <SessionExpiredModal
        visible={showExpireModal}
        onClose={() => {
          setShowExpireModal(false);
        }}
      />
    </SafeAreaView>
  );
}
